import { Component, OnDestroy } from '@angular/core';
import { combineLatest, map, Subject, takeUntil } from 'rxjs';

import { TriggerAction } from '@shared/actions';
import { ButtonOutline } from '@shared/buttons/button-outline/button-outline.data';
import { CornerstoneService } from '@viewer/cornerstone.service';
import { StateManagerProjectService } from '@state-manager/state-manager-project.service';
import { ConfirmDialogService } from '@shared/confirm-dialog';
import {
  ConfirmDialogAnswer,
  ConfirmDialogResults,
  TextsForErrorsDialogs,
} from '@shared/confirm-dialog/confirm-dialog.data';
import {
  ProjectActionsRequestService,
  TimeTrackerService,
} from '@viewer/services';
import { CornerstoneTool } from '@viewer/data';
import { LogMessageAction } from '@viewer/services/log-event.data';
import { OCCViewerService } from '@viewer/occ-viewer/occ-viewer.service';

@Component({
  selector: 'app-approve-action',
  templateUrl: 'approve-action.component.html',
  styleUrls: ['approve-action.component.scss'],
})
export class ApproveActionComponent implements OnDestroy {
  readonly ButtonOutline = ButtonOutline;

  public approveAction: TriggerAction;

  private _unsubscribe$ = new Subject();
  displaySpinner: boolean = false;
  constructor(
    private _cornerstoneService: CornerstoneService,
    public stateManagerProjectService: StateManagerProjectService,
    private _confirmDialogService: ConfirmDialogService,
    private _projectActionsRequestService: ProjectActionsRequestService,
    private _timeTrackerService: TimeTrackerService,
    private _confirmDialog: ConfirmDialogService,
    private _occViewerService: OCCViewerService,
  ) {
    this.approveAction = new TriggerAction('', 'Actions.APPROVE', () => {
      this._confirmDialogService
        .openConfirmDialog(
          'Dialog.APPROVE_FILE_MESSAGE',
          'Dialog.RejectButtons.CANCEL',
          'Dialog.ConfirmButtons.APPROVE'
        )
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe((res: ConfirmDialogAnswer) => {
          if (res.result === ConfirmDialogResults[1]) {
            this.displaySpinner = true;
            this.stateManagerProjectService.isDisabledAllToolsForChangeMask =
              true;
            this.stateManagerProjectService.isApprovedClicked = true;
            this._cornerstoneService.saveMaskV2(
              this.handleAfterGenerateBoneModel.bind(this), // Callback if needed
              false, // isApproved
              false // isDisableButtonAfterComplete
            ).then(() => {
            }).catch((error) => {
            });
          }
        });
    });

    this.approveAction.isEnable = () =>
      combineLatest([
        this.stateManagerProjectService.isViewerLoaded$,
        this.stateManagerProjectService.isDisabledAllToolsForChangeMask$,
      ]).pipe(
        map(
          ([isViewerLoaded, isDisabledAllToolsForChangeMask]) =>
            isViewerLoaded &&
            this.checkPercentsOfViewed() &&
            !this.stateManagerProjectService.is3dUpdateNeeded &&
            !isDisabledAllToolsForChangeMask && 
            !this.stateManagerProjectService.isActiveShowChangesMode
        )
      );
  }

  private handleAfterGenerateBoneModel(): void {
    const callbackForApprovedSave = () => {
      console.log('/updatecasestatus api triggered');
      this._projectActionsRequestService
        .requestUpdateCaseStatus(
          this.stateManagerProjectService.caseId,
          1
        )
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe({
          next: () => {
            console.log('/auditlog api triggered');
            this._projectActionsRequestService
              .requestSaveLogForCase(
                this.stateManagerProjectService.caseId,
                this.stateManagerProjectService.currentUserName,
                LogMessageAction.CaseApproved,
                new Date().toISOString(),
                1,
                'UPDATE'
              )
              .subscribe({
                next: () => {
                    this._timeTrackerService.destroyObservable().subscribe({
                      next: (res) => {
                        this._timeTrackerService.timeRefresh();
                        this._projectActionsRequestService.redirectToCaseList();
                      }, 
                      error: (err) => {
                        this._confirmDialog
                        .openConfirmDialog(
                          TextsForErrorsDialogs.PROCESSING_ERROR_PLEASE_REFRESH,
                          TextsForErrorsDialogs.CLOSE,
                          ''
                        )
                        .subscribe((_) => { });
                      }
                    });
                  this.displaySpinner = false;
                },
                error: () => {
                  this._showErrorMessage();
                },
              });
          },
          error: () => {
            this._showErrorMessage();
            // this._timeTrackerService.init();
            this.stateManagerProjectService.isDisabledAllToolsForChangeMask =
              !this.stateManagerProjectService.isEditAllowed;
          },
        });
    };

    this._cornerstoneService.toolActivate(CornerstoneTool.EmptyTool);
      this._cornerstoneService
        .saveMask(undefined, true, callbackForApprovedSave)
        .then((_) => { })
        .catch((_) => {
          this._showErrorMessage();
          // this._timeTrackerService.init();
          this.stateManagerProjectService.isDisabledAllToolsForChangeMask =
            !this.stateManagerProjectService.isEditAllowed;
        });
  }

  public checkPercentsOfViewed(): boolean {
    return (
      this._calculateOfViewedSliceForWindow(1) === 100 ||
      this._calculateOfViewedSliceForWindow(2) === 100 ||
      this._calculateOfViewedSliceForWindow(3) === 100
    );
  }

  private _calculateOfViewedSliceForWindow(windowIndex: number): number {
    const map = this._cornerstoneService.getMapOfViewedSlices(windowIndex);
    if (!map) {
      return 0;
    }

    return Math.trunc(
      (map.filter((el) => el === true).length / map.length) * 100
    );
  }

  private _showErrorMessage(): void {
    this.displaySpinner = false;
    this._confirmDialog
      .openConfirmDialog(
        TextsForErrorsDialogs.PROCESSING_ERROR_PLEASE_REFRESH,
        TextsForErrorsDialogs.CLOSE,
        ''
      )
      .subscribe((_) => { });
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next(undefined);
    this._unsubscribe$.complete();
  }
}
